// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.6.3, gate E12.5 pass 2 — where a LIVE session held at the TOTP
 * step-up (403 `MFA_REQUIRED`) enters its code.
 *
 * One definition for the route, for the API paths that must never trigger the
 * redirect to it, and for building the address, so the api-client (which
 * detects the hold), the redirector (which navigates), the login/handoff pages
 * and `ProtectedRoute` (which route to it directly) cannot drift apart.
 */

import { safeNextPath } from './safe-next-path.js';

/** The page that takes a code. Public-shell, outside `ProtectedRoute`. */
export const MFA_STEP_UP_ROUTE = '/mfa-step-up';

/**
 * API paths (relative to the API base, no query) whose 403 `MFA_REQUIRED` never
 * moves the window: the step-up page's own call, and the two paths a held
 * session is always admitted to (`mfaHoldAllowedPaths('verification')` in
 * `middleware/auth.ts`).
 */
export const MFA_STEP_UP_EXEMPT_PATHS: readonly string[] = [
  '/mfa/verify',
  '/auth/logout',
  '/auth/me',
];

/**
 * The step-up address for a person who was going to `next`. `next` is honoured
 * only as a same-origin path (`safeNextPath`, the rule the login and enrolment
 * pages use); anything else is dropped and the page falls back to `/admin`.
 */
export function mfaStepUpPath(next: string | null | undefined): string {
  const safe = safeNextPath(next ?? null);
  return safe ? `${MFA_STEP_UP_ROUTE}?next=${encodeURIComponent(safe)}` : MFA_STEP_UP_ROUTE;
}
